import type { Participacao } from '@/types/evento-publico';
import { ehGrupoEmDuasEtapas } from '@/lib/grupoOpcao';
import { textoDoValor, vagasRestantes, fraseEmBreve } from '../utils';
import GruposDuasEtapas from './GruposDuasEtapas';
import type { ProximoLote } from '../lotes';

function diaMes(iso: string): string {
  const d = new Date(iso);
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}`;
}

export function chaveOpcao(p: Participacao): string {
  return `${p.modalidade_id}:${p.opcao_id ?? ''}`;
}

/** Configuração de vagas/suplentes por modalidade. */
export interface ConfigModalidade {
  aceita_suplentes: boolean;
  situacao_visivel: boolean;
}

/** Esgotado mas ainda aceita suplentes: dá para entrar na fila. */
export function aceitaComoSuplente(
  p: Participacao,
  cfg?: ConfigModalidade | null,
): boolean {
  return p.situacao === 'esgotado' && cfg?.aceita_suplentes === true;
}

/** Texto do que o botão faz quando a vaga acabou. */
export function rotuloSuplente(cfg?: ConfigModalidade | null): string {
  return cfg?.situacao_visivel === false ? 'Inscrever-se' : 'Entrar como suplente';
}

interface EscolherParticipacaoProps {
  grupos: Participacao[][];
  vendeIngressos: boolean;
  desabilitado: boolean;
  motivoPorModalidade: Map<number, string | null>;
  selecionada: string | null;
  onSelecionar: (chave: string) => void;
  proximoLote?: ProximoLote | null;
  /** Mostra "+ taxa" ao lado do valor (o comprador paga a taxa). */
  mostraTaxa?: boolean;
  /** aceita_suplentes / situacao_visivel por modalidade. */
  configPorModalidade?: Map<number, ConfigModalidade>;
}

function semTags(texto: string): string {
  return texto
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

// Opções de inscrição/ingresso como cartões com rádio. A selecionada fica com
// borda azul de 2 px e fundo azul claro. Cada modalidade é um bloco.
export default function EscolherParticipacao({
  grupos,
  vendeIngressos,
  desabilitado,
  motivoPorModalidade,
  selecionada,
  onSelecionar,
  proximoLote = null,
  mostraTaxa = false,
  configPorModalidade,
}: EscolherParticipacaoProps) {
  const nenhumAberto = grupos.every((lista) =>
    lista.every(
      (p) =>
        p.situacao !== 'abertas' &&
        !aceitaComoSuplente(p, configPorModalidade?.get(p.modalidade_id)),
    ),
  );

  return (
    <div className="space-y-6">
      {grupos.map((lista) => {
        const primeira = lista[0];
        const temGrupos = primeira.opcao_id !== null;
        const motivo = motivoPorModalidade.get(primeira.modalidade_id) ?? null;

        return (
          <div key={primeira.modalidade_id} className="app-entra">
            <div className="flex items-center justify-between gap-3">
              <h3 className="min-w-0 truncate text-[17px] font-extrabold text-foreground-950">
                {primeira.modalidade}
              </h3>
              {!primeira.publica && (
                <span className="shrink-0 whitespace-nowrap rounded-full bg-secondary-100 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-secondary-700">
                  Por convite
                </span>
              )}
            </div>

            {primeira.descricao && (
              <p className="mt-1 line-clamp-2 text-[14px] leading-relaxed text-foreground-600">
                {semTags(primeira.descricao)}
              </p>
            )}

            {ehGrupoEmDuasEtapas(lista) ? (
              <div className="mt-3">
                <GruposDuasEtapas
                  lista={lista}
                  selecionada={selecionada}
                  onSelecionar={onSelecionar}
                  vendeIngressos={vendeIngressos}
                  desabilitado={desabilitado}
                  mostraTaxa={mostraTaxa}
                  configPorModalidade={configPorModalidade}
                />
              </div>
            ) : (
            <div className="mt-3 space-y-3">
              {lista.map((p) => {
                const cfg = configPorModalidade?.get(p.modalidade_id) ?? null;
                const suplente = aceitaComoSuplente(p, cfg);
                const escondido = suplente && cfg?.situacao_visivel === false;
                const ativo = (p.situacao === 'abertas' || suplente) && !desabilitado;
                const chave = chaveOpcao(p);
                const selecionado = ativo && selecionada === chave;
                const vagas = vagasRestantes(p.restantes, vendeIngressos);

                return (
                  <button
                    key={p.opcao_id ?? p.modalidade_id}
                    type="button"
                    disabled={!ativo}
                    onClick={() => ativo && onSelecionar(chave)}
                    className={`aperta flex w-full items-center justify-between gap-3 rounded-[18px] border-2 p-4 text-left transition-colors ${
                      selecionado
                        ? 'border-primary-500 bg-primary-50'
                        : ativo
                          ? 'cursor-pointer border-background-200 bg-background-50 shadow-pcard'
                          : 'cursor-not-allowed border-background-200 bg-background-100 opacity-60'
                    }`}
                  >
                    <div className="min-w-0">
                      {temGrupos && (
                        <p className="truncate text-[15px] font-bold text-foreground-950">
                          {p.grupo}
                        </p>
                      )}
                      <p
                        className={`truncate ${
                          temGrupos
                            ? 'mt-0.5 text-[14px] font-semibold text-foreground-600'
                            : 'text-[16px] font-extrabold text-foreground-950'
                        }`}
                      >
                        {textoDoValor(p)}
                      </p>
                      {mostraTaxa && ativo && (
                        <p className="mt-0.5 text-[12px] font-medium text-foreground-500">
                          + taxa de serviço
                        </p>
                      )}
                      {ativo && vagas && (
                        <p className="mt-1 text-[12px] font-semibold text-warning-600">{vagas}</p>
                      )}
                      {suplente && !escondido && (
                        <p className="mt-1 text-[12px] font-semibold text-warning-600">
                          Vagas esgotadas · entre como suplente
                        </p>
                      )}
                      {!ativo && (
                        <p className="mt-1 text-[12px] font-medium text-foreground-400">
                          {p.situacao === 'em_breve'
                            ? 'Em breve'
                            : p.situacao === 'esgotado'
                              ? 'Esgotado'
                              : 'Encerradas'}
                        </p>
                      )}
                    </div>

                    <span
                      className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full border-2 ${
                        selecionado
                          ? 'border-primary-500 bg-primary-500 text-white'
                          : 'border-background-300 text-transparent'
                      }`}
                    >
                      <i className="ri-check-line text-sm" aria-hidden />
                    </span>
                  </button>
                );
              })}
            </div>
            )}

            {primeira.situacao === 'em_breve' && (
              <p className="mt-2 text-[13px] text-foreground-500">
                {fraseEmBreve(motivo, primeira.inscricoes_abrem_em)}
              </p>
            )}
          </div>
        );
      })}

      {proximoLote && (
        <p className="text-[13px] text-foreground-500">
          {proximoLote.nome}:{" "}
          {textoDoValor({
            valor_situacao: proximoLote.valorSituacao,
            valor_centavos: proximoLote.valorCentavos,
          })}{' '}
          a partir de {diaMes(proximoLote.abreEm)}
        </p>
      )}

      {nenhumAberto && !desabilitado && (
        <p className="text-[14px] text-foreground-500">
          {vendeIngressos
            ? 'Ingressos esgotados ou fora do período de venda.'
            : 'As inscrições para este evento ainda não estão disponíveis.'}
        </p>
      )}
    </div>
  );
}